/**
 * Cards shown under an assistant answer. Nothing on a card acts by itself: each one needs the
 * owner's explicit button (approve a proposal, save a decision, finalise a lesson), and a card
 * built on trial data can never be approved. Stored in conversation_messages.cards and read
 * back by the app through parseCards, so both sides agree on one shape.
 */
export const PROPOSAL_KINDS = [
  'launch',
  'budget_change',
  'pause',
  'resume',
  'creative_update',
  'negative_keywords',
  'language_change',
] as const;
export type ProposalKind = (typeof PROPOSAL_KINDS)[number];

export const DECISION_TOPICS = [
  'budget',
  'branch',
  'campaign',
  'creative',
  'audience',
  'timing',
  'other',
] as const;
export type DecisionTopic = (typeof DECISION_TOPICS)[number];

/** One branch's daily budget before and after a budget change, in fils. */
export interface BranchBudgetFigure {
  readonly code: string;
  readonly beforeFils: number;
  readonly afterFils: number;
}

/**
 * The numbers the owner approves, always computed by the server: for a live proposal they are
 * read from the stored row (the plan and the database's own cap check), for a trial card from
 * the tool's computation. The model's title, summary and reason are commentary next to them.
 */
export interface ProposalFigures {
  /** budget_change: every branch whose daily budget changes. */
  readonly branches: readonly BranchBudgetFigure[];
  /** launch and resume: the branches the new daily spend goes to. */
  readonly addedBranchCodes: readonly string[];
  /** Change to the daily total (negative when it falls). */
  readonly dailyChangeFils: number;
  /** The same change over the days left in the month, today included. */
  readonly monthChangeFils: number;
  readonly projectedMonthFils: number;
  readonly monthlyCapFils: number;
}

interface ProposalCardBase {
  readonly type: 'proposal';
  readonly kind: ProposalKind;
  readonly title: string;
  readonly summary: string;
  readonly rationale: string;
}

/**
 * A live card points at the stored proposal (its figures, status and approval are read from the
 * row); a trial card carries the figures it was shown with and can never be approved.
 */
export type ProposalCard =
  | (ProposalCardBase & { readonly mock: false; readonly proposalId: string })
  | (ProposalCardBase & {
      readonly mock: true;
      readonly proposalId: null;
      readonly figures: ProposalFigures;
    });

export interface DecisionCard {
  readonly type: 'decision';
  readonly topic: DecisionTopic;
  readonly decision: string;
  readonly reason: string;
  readonly branchCode: string | null;
}

export interface LessonCard {
  readonly type: 'lesson';
  readonly lessonId: string;
  readonly campaign: string;
  readonly goal: string;
  readonly outcome: 'met' | 'partly_met' | 'missed';
}

/** Ad texts that passed every Google limit (check_ad_texts), ready to copy into a proposal. */
export interface AdCopyCard {
  readonly type: 'ad_copy';
  readonly format: 'pmax' | 'search';
  readonly headlines: readonly string[];
  readonly longHeadlines: readonly string[];
  readonly descriptions: readonly string[];
}

export interface BriefShot {
  readonly subject: string;
  readonly orientation: 'landscape' | 'square' | 'portrait';
  readonly notes: string;
}

/** A photo brief for the design team, with Google's image sizes. */
export interface BriefCard {
  readonly type: 'brief';
  readonly title: string;
  readonly goal: string;
  readonly branchCode: string | null;
  readonly shots: readonly BriefShot[];
}

export type Card = ProposalCard | DecisionCard | LessonCard | AdCopyCard | BriefCard;

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): v is string => typeof v === 'string';
const oneOf = <T extends string>(list: readonly T[], v: unknown): v is T =>
  typeof v === 'string' && (list as readonly string[]).includes(v);
const strings = (v: unknown): v is string[] => Array.isArray(v) && v.every(str);
const ORIENTATIONS = ['landscape', 'square', 'portrait'] as const;
const shot = (v: unknown): BriefShot | null =>
  isRecord(v) && str(v['subject']) && oneOf(ORIENTATIONS, v['orientation']) && str(v['notes'])
    ? { subject: v['subject'], orientation: v['orientation'], notes: v['notes'] }
    : null;

const int = (v: unknown): v is number => typeof v === 'number' && Number.isSafeInteger(v);
const fils = (v: unknown): v is number => int(v) && v >= 0;

function parseFigures(v: unknown): ProposalFigures | null {
  if (!isRecord(v) || !Array.isArray(v['branches']) || !strings(v['addedBranchCodes'])) return null;
  const branches = v['branches'].map((b): BranchBudgetFigure | null =>
    isRecord(b) && str(b['code']) && fils(b['beforeFils']) && fils(b['afterFils'])
      ? { code: b['code'], beforeFils: b['beforeFils'], afterFils: b['afterFils'] }
      : null,
  );
  return branches.every((b): b is BranchBudgetFigure => b !== null) &&
    int(v['dailyChangeFils']) &&
    int(v['monthChangeFils']) &&
    fils(v['projectedMonthFils']) &&
    fils(v['monthlyCapFils'])
    ? {
        branches,
        addedBranchCodes: v['addedBranchCodes'],
        dailyChangeFils: v['dailyChangeFils'],
        monthChangeFils: v['monthChangeFils'],
        projectedMonthFils: v['projectedMonthFils'],
        monthlyCapFils: v['monthlyCapFils'],
      }
    : null;
}

/**
 * The figures of a stored proposal, from its row (plan, the generated daily change and the cap
 * check the database ran). Null when the row lacks any of them: then nothing can be approved.
 */
export function proposalFiguresFromRow(row: {
  readonly kind: string;
  readonly plan: unknown;
  readonly daily_change_fils: number | null;
  readonly month_change_fils: number | null;
  readonly projected_month_fils: number | null;
  readonly monthly_cap_fils: number | null;
}): ProposalFigures | null {
  const plan = isRecord(row.plan) ? row.plan : null;
  if (plan === null) return null;
  const budgets = row.kind === 'budget_change' ? plan['branch_budgets'] : [];
  const added = row.kind === 'launch' || row.kind === 'resume' ? plan['branch_codes'] : [];
  if (!Array.isArray(budgets)) return null;
  return parseFigures({
    branches: budgets.map((b: unknown) =>
      isRecord(b)
        ? {
            code: b['branch_code'],
            beforeFils: b['current_daily_fils'],
            afterFils: b['new_daily_fils'],
          }
        : null,
    ),
    addedBranchCodes: added,
    dailyChangeFils: row.daily_change_fils,
    monthChangeFils: row.month_change_fils,
    projectedMonthFils: row.projected_month_fils,
    monthlyCapFils: row.monthly_cap_fils,
  });
}

function parseCard(v: unknown): Card | null {
  if (!isRecord(v)) return null;
  switch (v['type']) {
    case 'proposal': {
      if (
        !oneOf(PROPOSAL_KINDS, v['kind']) ||
        !str(v['title']) ||
        !str(v['summary']) ||
        !str(v['rationale'])
      )
        return null;
      const base = {
        type: 'proposal' as const,
        kind: v['kind'],
        title: v['title'],
        summary: v['summary'],
        rationale: v['rationale'],
      };
      if (v['mock'] === false && str(v['proposalId'])) {
        return { ...base, mock: false, proposalId: v['proposalId'] };
      }
      const figures = parseFigures(v['figures']);
      return v['mock'] === true && v['proposalId'] === null && figures !== null
        ? { ...base, mock: true, proposalId: null, figures }
        : null;
    }
    case 'decision':
      return oneOf(DECISION_TOPICS, v['topic']) &&
        str(v['decision']) &&
        str(v['reason']) &&
        (v['branchCode'] === null || str(v['branchCode']))
        ? {
            type: 'decision',
            topic: v['topic'],
            decision: v['decision'],
            reason: v['reason'],
            branchCode: v['branchCode'],
          }
        : null;
    case 'lesson':
      return str(v['lessonId']) &&
        str(v['campaign']) &&
        str(v['goal']) &&
        oneOf(['met', 'partly_met', 'missed'] as const, v['outcome'])
        ? {
            type: 'lesson',
            lessonId: v['lessonId'],
            campaign: v['campaign'],
            goal: v['goal'],
            outcome: v['outcome'],
          }
        : null;
    case 'ad_copy':
      return oneOf(['pmax', 'search'] as const, v['format']) &&
        strings(v['headlines']) &&
        strings(v['longHeadlines']) &&
        strings(v['descriptions'])
        ? {
            type: 'ad_copy',
            format: v['format'],
            headlines: v['headlines'],
            longHeadlines: v['longHeadlines'],
            descriptions: v['descriptions'],
          }
        : null;
    case 'brief': {
      const shots = Array.isArray(v['shots']) ? v['shots'].map(shot) : null;
      return str(v['title']) &&
        str(v['goal']) &&
        (v['branchCode'] === null || str(v['branchCode'])) &&
        shots !== null &&
        shots.length > 0 &&
        shots.every((s): s is BriefShot => s !== null)
        ? { type: 'brief', title: v['title'], goal: v['goal'], branchCode: v['branchCode'], shots }
        : null;
    }
    default:
      return null;
  }
}

/** Reads stored cards; anything malformed is dropped rather than shown half-right. */
export function parseCards(raw: unknown): Card[] {
  return Array.isArray(raw) ? raw.map(parseCard).filter((c): c is Card => c !== null) : [];
}

/** Ids of the live proposals on these answers' cards, each once. */
export function proposalIdsIn(cards: readonly (readonly Card[])[]): string[] {
  return [
    ...new Set(
      cards.flat().flatMap((c) => (c.type === 'proposal' && !c.mock ? [c.proposalId] : [])),
    ),
  ];
}
